export type PagedListParams = {
  offset?: number | string;
  limit?: number | string;
  q?: string | string[];
  sort?: string;
  direction?: 'asc' | 'desc';
  // allow endpoint-specific extras (e.g., period_end)
  [key: string]: string | number | string[] | undefined;
};

/**
 * Safely merges existing query params in `rawBase` with `params`.
 * - Overwrites empty values (e.g., offset=) with provided ones.
 * - Drops undefined/empty-string values.
 * - Emits only one '?' and a clean query string.
 */
export function buildCollectionUrl(
  rawBase: string,
  params?: PagedListParams
): string {
  const [basePath, existingQuery = ''] = rawBase.split('?');
  const search = new URLSearchParams(existingQuery);

  const setIf = (key: string, val?: string | number) => {
    if (val === undefined || val === null || String(val).trim() === '') {
      search.delete(key);
    } else {
      search.set(key, String(val));
    }
  };

  if (params) {
    const CORE = new Set(['offset', 'limit', 'sort', 'direction', 'q']);

    // core
    setIf('offset', params.offset);
    setIf('limit', params.limit);
    setIf('sort', params.sort);
    setIf('direction', params.direction);

    // q can be string or string[] — emit repeated q= params for arrays
    search.delete('q');
    if (Array.isArray(params.q)) {
      params.q.filter(Boolean).forEach(v => search.append('q', v));
    } else if (params.q) {
      search.append('q', params.q);
    }

    // forward any extra params (e.g., period_end)
    for (const [k, v] of Object.entries(params)) {
      if (CORE.has(k)) continue;
      if (Array.isArray(v)) {
        const joined = (v as (string | number)[]).filter(Boolean).join(',');
        setIf(k, joined);
      } else {
        setIf(k, v as string | number | undefined);
      }
    }
  }

  // clean empties (incl. from base url)
  for (const [k, v] of Array.from(search.entries())) {
    if (v === undefined || v === null || String(v).trim() === '') {
      search.delete(k);
    }
  }

  const qs = search.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}
